/**
 * EditOnboardingDialog Component
 *
 * Dialog for editing onboarding settings including name and language configuration.
 * Shows warning when removing languages (locale content will be lost).
 *
 * Architecture: Onboarding is the source of truth for languages.
 * When languages change, all screens in latestVersion get A+B transformation.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  Alert,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import type { Onboarding, OnboardingVersion } from '@modules/firebase-core/converters/onboardingConverter';
import { getLanguagesToRemove, getLanguagesToAdd } from '../utils/languageDiff';
import { Language, LANGUAGES, getLanguageName } from '../constants/languages';

/**
 * Props for EditOnboardingDialog
 */
export interface EditOnboardingDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when onboarding is saved */
  onSave: (data: {
    name: string;
    defaultLanguage: Language;
    languages: Language[];
  }) => Promise<void>;
  /** Current onboarding data */
  onboarding: Onboarding | null;
  /** Current latest version (for language fallback) */
  latestVersion: OnboardingVersion | null;
  /** Loading state from parent */
  loading?: boolean;
}

/**
 * EditOnboardingDialog Component
 */
export function EditOnboardingDialog({
  open,
  onClose,
  onSave,
  onboarding,
  latestVersion,
  loading: externalLoading = false,
}: EditOnboardingDialogProps) {
  // Form state
  const [name, setName] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState<Language | ''>('');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  const loading = externalLoading || internalLoading;

  // Get current languages (from Onboarding or fallback to Version).
  // Firestore stores language codes as plain strings — narrow them to Language here.
  const currentLanguages = useMemo<Language[]>(() => {
    if (onboarding?.languages) return onboarding.languages as Language[];
    if (latestVersion?.screensGraph.languages) return latestVersion.screensGraph.languages as Language[];
    return [Language.EnUs];
  }, [onboarding, latestVersion]);

  const currentDefaultLanguage = useMemo<Language>(() => {
    if (onboarding?.defaultLanguage) return onboarding.defaultLanguage as Language;
    if (latestVersion?.screensGraph.defaultLanguage) return latestVersion.screensGraph.defaultLanguage as Language;
    return Language.EnUs;
  }, [onboarding, latestVersion]);

  // Initialize form when dialog opens or onboarding changes
  useEffect(() => {
    if (open && onboarding) {
      setName(onboarding.name);
      setDefaultLanguage(currentDefaultLanguage);
      setLanguages(currentLanguages);
      setError(null);
      setShowWarning(false);
    }
  }, [open, onboarding, currentDefaultLanguage, currentLanguages]);

  // Calculate languages to be removed (for warning)
  const languagesToRemove = useMemo(() => {
    return getLanguagesToRemove(currentLanguages, languages);
  }, [currentLanguages, languages]);

  // Calculate languages to be added
  const languagesToAdd = useMemo(() => {
    return getLanguagesToAdd(currentLanguages, languages);
  }, [currentLanguages, languages]);

  // Detect full language replacement (no overlap between old and new)
  const isFullReplacement = useMemo(() => {
    if (languagesToAdd.length === 0 || languagesToRemove.length === 0) return false;
    const currentSet = new Set(currentLanguages);
    return !languages.some(lang => currentSet.has(lang));
  }, [currentLanguages, languages, languagesToAdd.length, languagesToRemove.length]);

  // Form validation
  const isValid = name.trim().length > 0 && defaultLanguage !== '';

  // Handle default language change
  const handleDefaultLanguageChange = useCallback((event: SelectChangeEvent<Language | ''>) => {
    const newDefault = event.target.value as Language;
    setDefaultLanguage(newDefault);
    // Auto-add default language to languages list
    if (newDefault && !languages.includes(newDefault)) {
      setLanguages(prev => [...prev, newDefault]);
    }
  }, [languages]);

  // Handle languages change
  const handleLanguagesChange = useCallback((event: SelectChangeEvent<Language[]>) => {
    const value = event.target.value;
    const newLanguages = typeof value === 'string' ? (value.split(',') as Language[]) : value;

    // Ensure default language is always included
    if (defaultLanguage && !newLanguages.includes(defaultLanguage)) {
      setLanguages([defaultLanguage, ...newLanguages.filter(l => l !== defaultLanguage)]);
    } else {
      setLanguages(newLanguages);
    }
  }, [defaultLanguage]);

  // Handle form submission
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isValid || !defaultLanguage) return;

    // Show warning if languages are being removed
    if (languagesToRemove.length > 0 && !showWarning) {
      setShowWarning(true);
      return;
    }

    setInternalLoading(true);
    setError(null);

    try {
      // Ensure default language is in languages list
      const finalLanguages = languages.includes(defaultLanguage)
        ? languages
        : [defaultLanguage, ...languages];

      await onSave({
        name: name.trim(),
        defaultLanguage: defaultLanguage as Language,
        languages: finalLanguages,
      });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update onboarding';
      setError(message);
      setShowWarning(false);
    } finally {
      setInternalLoading(false);
    }
  }, [name, defaultLanguage, languages, isValid, languagesToRemove.length, showWarning, onSave, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setShowWarning(false);
    onClose();
  }, [onClose]);

  // Handle back from warning
  const handleBackFromWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  if (!onboarding) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit Onboarding</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Warning about removing languages */}
            {showWarning && languagesToRemove.length > 0 && (
              <Alert severity="warning">
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  The following languages will be removed:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {languagesToRemove.map((code) => (
                    <Chip
                      key={code}
                      label={getLanguageName(code)}
                      size="small"
                      color="warning"
                    />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  All localized content for these languages will be permanently deleted from all screens.
                  This action cannot be undone.
                </Typography>
              </Alert>
            )}

            {/* Warning about full language replacement (no overlap) */}
            {showWarning && isFullReplacement && (
              <Alert severity="warning">
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Complete language replacement detected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Since none of the new languages exist in the current content,
                  all new locales will be initialized with content from the first
                  available existing language. Please review and translate the
                  content after saving.
                </Typography>
              </Alert>
            )}

            {/* Info about adding languages */}
            {languagesToAdd.length > 0 && !showWarning && (
              <Alert severity="info">
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  New languages will be added:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {languagesToAdd.map((code) => (
                    <Chip
                      key={code}
                      label={getLanguageName(code)}
                      size="small"
                      color="info"
                    />
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Content will be initialized with values from the default language.
                </Typography>
              </Alert>
            )}

            <TextField
              autoFocus
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              disabled={loading || showWarning}
            />

            <FormControl fullWidth required disabled={loading || showWarning}>
              <InputLabel id="edit-default-language-label">Default Language</InputLabel>
              <Select
                labelId="edit-default-language-label"
                label="Default Language"
                value={defaultLanguage}
                onChange={handleDefaultLanguageChange}
              >
                {LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={loading || showWarning}>
              <InputLabel id="edit-languages-label">Supported Languages</InputLabel>
              <Select<Language[]>
                labelId="edit-languages-label"
                label="Supported Languages"
                multiple
                value={languages}
                onChange={handleLanguagesChange}
                input={<OutlinedInput label="Supported Languages" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((code) => {
                      const lang = LANGUAGES.find(l => l.code === code);
                      const isDefault = code === defaultLanguage;
                      return (
                        <Chip
                          key={code}
                          label={lang?.name || code}
                          size="small"
                          color={isDefault ? 'primary' : 'default'}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                    {lang.code === defaultLanguage && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        (default)
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          {showWarning ? (
            <>
              <Button onClick={handleBackFromWarning} disabled={loading}>
                Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="warning"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Confirm & Save'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!isValid || loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditOnboardingDialog;
